import { DateTime } from 'luxon';

/**
 * A badge has two clocks, and they mean different things.
 *
 * - lastSeenTimestamp: the last message of any kind. A connected badge sends
 *   a heartbeat about every 600 seconds, so this is what "connected" reads.
 * - lastPositionTimestamp: the last position update. A badge that is not
 *   moving sends none, so one left on a desk has an old position time and is
 *   still perfectly well connected.
 *
 * Shown as one "last seen", the pair looked like a contradiction: Connected,
 * last seen five hours ago. These say which clock is which.
 */

/**
 * Minutes without any message before a badge counts as not connected: three
 * missed heartbeats. Two (20 minutes) would flip a badge to offline whenever
 * one heartbeat ran a little late.
 */
export const BADGE_ONLINE_MINUTES = 30;

/** Time today, or date and time when it was not today. */
export const formatWhen = (dateTime) => {
  if (dateTime == null) return null;
  const local = dateTime.toLocal();
  return local.hasSame(DateTime.now(), 'day')
    ? local.toLocaleString(DateTime.TIME_SIMPLE)
    : local.toLocaleString(DateTime.DATETIME_SHORT);
};

/**
 * Where the badge last moved to and since when: "Main Office · since 5:14 AM".
 * Null when it has no position yet.
 *
 * @param {import('.').Badge | null | undefined} badge
 */
export const locationOf = (badge) => {
  const room = badge?.getLastSeenRoom()?.getIdentifier();
  const since = formatWhen(badge?.getLastPositionTimestamp());
  if (!room || !since) return null;
  return room + ' · since ' + since;
};

/**
 * How long ago the badge was last heard from: "1 minute ago". Null when it
 * has never reported.
 *
 * @param {import('.').Badge | null | undefined} badge
 */
export const heartbeatOf = (badge) => badge?.getLastSeenTimestamp()?.toRelative() ?? null;
